import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { useSchemas } from '@atom/hooks/useSchemas';
import { Icon, Modal, Progress, Select, Switch, TextField } from '@atom/mui';
import { MonetizationOnIcon } from '@atom/mui/icons';
import { budgetUnitsSelector } from '@atom/selectors/preferencesSelectors';
import { PolicyAction } from '@atom/types/policy';
import {
  SchemaBudgetType,
  StartEndReadingOption,
  StockBasedOption,
} from '@atom/types/schema';
import { hasAccess } from '@atom/utilities/accessUtilities';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import {
  isAdminRequiredForAssetQuantityEditing,
  isAssetBudgetEditingDisabled,
} from '@atom/utilities/tenantFeatureUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import InventoryDetailContext from '../InventoryDetailContext';
import useAssetUsage from '../useAssetUsage';

import classes from './header.module.css';

const { MenuItem } = Select;

const styles = {
  icon: {
    marginRight: '1.5rem',
  },
  unitOption: {
    textTransform: 'uppercase',
  },
};

export type EditAssetModalProps = {
  onAssetEdit: (
    id: string,
    name: string,
    rate?: number,
    unit?: string,
    isStockBased?: boolean,
    isStartEndReading?: boolean,
    isStartEndCalculated?: boolean,
    quantityRemaining?: number,
  ) => void;
  open: boolean;
  closeModal: () => void;
};

const EditAssetModal = ({
  onAssetEdit,
  open,
  closeModal,
}: EditAssetModalProps) => {
  const { asset } = useContext(InventoryDetailContext);

  const budgetUnits = useSelector(budgetUnitsSelector);

  const { schemas = [], loading } = useSchemas();

  const [name, setName] = useState<string>(asset.name);
  const [rate, setRate] = useState<number>(asset?.budget?.rate);
  const [unit, setUnit] = useState<string>(asset?.budget?.unit);
  const [isStockBased, setIsStockBased] = useState<boolean>(false);
  const [isStartEndReading, setIsStartEndReading] = useState<boolean>(false);
  const [isStartEndCalculated, setIsStartEndCalculated] =
    useState<boolean>(false);
  const [quantityRemaining, setQuantityRemaining] = useState<number>(0);

  const {
    summary,
    summaryLoading: usageSummaryLoading,
    fetchSummary,
  } = useAssetUsage({ assetId: asset.id });

  useEffect(() => {
    if (!isNilOrEmpty(summary)) {
      setQuantityRemaining(summary.totalRemaining);
    }
  }, [summary]);

  useEffect(() => {
    if (open) {
      setName(asset?.name);
      setRate(asset?.budget?.rate);
      setUnit(asset?.budget?.unit);
      setIsStockBased(asset?.budget?.isStockBased);
      setIsStartEndReading(asset?.budget?.isStartEndReading);
      setIsStartEndCalculated(asset?.budget?.isStartEndCalculated);
      setQuantityRemaining(0);

      if (asset.isMaterial) {
        fetchSummary();
      }
    }
  }, [open]);

  const schema = useMemo(() => {
    return schemas.find(({ id }) => id === asset.schemaId);
  }, [schemas, asset.schemaId]);

  const updateRate = event => {
    const newTimeRate = event.target.value
      ? Math.abs(Number(event.target.value))
      : null;

    setRate(newTimeRate);
  };

  const updateQuantityRemaining = event => {
    const newQuantityRemaining = event.target.value
      ? Math.abs(Number(event.target.value))
      : null;

    setQuantityRemaining(newQuantityRemaining);
  };

  const confirm = () => {
    onAssetEdit(
      asset.id,
      name,
      rate,
      unit,
      isStockBased,
      isStartEndReading,
      isStartEndCalculated,
      quantityRemaining,
    );

    closeModal();
  };

  const canRename = hasAccess(PolicyAction.RENAME, asset.actions);
  const canUpdate = hasAccess(PolicyAction.UPDATE, asset.actions);

  const isStockBasedToggleDisabled =
    schema?.budget?.stockBasedOption === StockBasedOption.STOCK_BASED ||
    !canUpdate ||
    isAssetBudgetEditingDisabled;
  const isRateDisabled =
    asset?.budget?.type === SchemaBudgetType.FIXED ||
    !canUpdate ||
    isAssetBudgetEditingDisabled;
  const isUnitDisabled =
    asset?.budget?.type === SchemaBudgetType.FIXED ||
    asset?.budget?.type === SchemaBudgetType.VARIABLE_COST ||
    !canUpdate ||
    isAssetBudgetEditingDisabled ||
    (isAdminRequiredForAssetQuantityEditing &&
      doesNotHaveRolePermissions(ROLE_SETS.ADMIN));
  const isQuantityRemainingDisabled =
    usageSummaryLoading ||
    isAssetBudgetEditingDisabled ||
    (isAdminRequiredForAssetQuantityEditing &&
      doesNotHaveRolePermissions(ROLE_SETS.ADMIN));

  const showStockBased =
    schema?.budget?.stockBasedOption !== StockBasedOption.NONE;

  const showStartEndReading =
    !isStockBased &&
    schema?.budget?.startEndReadingOption !== StartEndReadingOption.NONE;

  const options = ['', ...budgetUnits];

  const isSaveDisabled =
    !name ||
    (asset?.isMaterial && !rate) ||
    (asset?.isMaterial && !unit) ||
    (asset?.isMaterial &&
      isStockBased &&
      !quantityRemaining &&
      usageSummaryLoading);

  return (
    <Modal
      open={open}
      title="Edit Inventory"
      loading={loading}
      onConfirm={confirm}
      onCancel={closeModal}
      confirmButtonText="Save"
      disabled={isSaveDisabled}
      ConfirmButtonProps={{ 'data-cy': 'editAssetModalSaveButton' }}
    >
      <div className={classes.inventoryRow}>
        <img style={styles.icon} src={renameIcon} />
        <TextField
          label="Name"
          value={name}
          onChange={event => setName(event.target.value)}
          disabled={!canRename}
          data-cy="editAssetModalNameInput"
        />
      </div>
      <div className={classes.inventoryRow}>
        <Icon style={styles.icon} icon={MonetizationOnIcon} />
        <div className={classes.costInputContainer}>
          <TextField
            label="Cost"
            value={rate}
            type="number"
            fullWidth
            onChange={updateRate}
            slotProps={{
              input: {
                startAdornment: '$',
              },
            }}
            disabled={isRateDisabled}
            data-cy="editAssetModalCostInput"
          />
          <div className={classes.separator}>per</div>
          <Select
            label="Unit"
            fullWidth
            InputProps={{
              style: styles.unitOption,
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setUnit(event.target.value)
            }
            value={unit}
            disabled={isUnitDisabled}
            data-cy="editAssetModalUnitSelect"
          >
            {options.map(option => {
              return (
                <MenuItem
                  key={option}
                  value={option || null}
                  style={styles.unitOption}
                >
                  {option}
                </MenuItem>
              );
            })}
          </Select>
        </div>
      </div>
      {showStockBased && (
        <>
          <div className={classes.inventoryRow}>
            <Switch
              checked={isStockBased}
              onChange={() => setIsStockBased(!isStockBased)}
              label="Stock-based Inventory"
              disabled={isStockBasedToggleDisabled}
              data-cy="editAssetModalStockBasedSwitch"
            />
          </div>
          {isStockBased && (
            <div className={clsx(classes.inventoryRow, classes.quantity)}>
              <TextField
                label="Total Remaining"
                value={quantityRemaining}
                type="number"
                fullWidth
                onChange={updateQuantityRemaining}
                disabled={isQuantityRemainingDisabled}
                data-cy="editAssetModalTotalRemainingInput"
                slotProps={{
                  input: {
                    ...(usageSummaryLoading && {
                      endAdornment: <Progress size={20} thickness={2} />,
                    }),
                  },
                }}
              />
              <div className={classes.quantitySuffix}>{unit}</div>
            </div>
          )}
        </>
      )}
      {showStartEndReading && (
        <>
          <div className={classes.inventoryRow}>
            <Switch
              checked={isStartEndReading}
              label="Enable Start and End Readings"
              disabled
              data-cy="editAssetModalStartEndReadingSwitch"
            />
          </div>
          {isStartEndReading && (
            <div className={classes.inventoryRow}>
              <Switch
                checked={isStartEndCalculated}
                label="Used Calculated from Start and End Readings"
                disabled
                data-cy="editAssetModalStartEndCalculatedSwitch"
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default EditAssetModal;
